import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { agreementService } from '../utils/api';
import { bodyToHtml } from '../lib/richText';
import { notify } from '../lib/notify';
import { ShieldCheck } from 'lucide-react';
import ModalSurface from './ui/ModalSurface';

// Roles that must accept agreements before using the portal. Office roles are
// never gated.
const GATED_ROLES = ['student', 'teacher'];

/**
 * AgreementGate wraps the authenticated app. For students and teachers it
 * fetches the college agreements they still have to accept and, if any are
 * pending, renders a full-screen blocking overlay with one checkbox per
 * agreement. The portal underneath stays mounted but is covered and
 * non-interactive until every box is ticked and consent is recorded.
 */
export default function AgreementGate({ children }) {
  const { user, isAuthenticated } = useAuth();
  const rawRole = user?.role?.name ?? user?.role ?? user?.roleName;
  const role = typeof rawRole === 'string' ? rawRole.toLowerCase() : '';
  const gated = isAuthenticated && GATED_ROLES.includes(role);

  const [pending, setPending] = useState([]);
  const [checked, setChecked] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchPending = useCallback(async () => {
    try {
      const res = await agreementService.pending();
      setPending(res.data?.agreements || []);
    } catch (_) {
      // Fail open on a network error — never lock a user out because the
      // agreements endpoint was briefly unreachable.
      setPending([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (gated) {
      setLoaded(false);
      fetchPending();
    } else {
      setPending([]);
      setLoaded(true);
    }
  }, [gated, user?.id, user?._id, fetchPending]);

  const allChecked = pending.length > 0 && pending.every((a) => checked[a._id]);

  const submit = async () => {
    if (!allChecked) return;
    setSubmitting(true);
    try {
      await agreementService.accept(pending.map((a) => a._id));
      // The portal underneath the gate already fired its data requests while the
      // user was still blocked — those came back 403 AGREEMENT_REQUIRED and left
      // stale error state on the page (the "يجب الموافقة على شروط الكلية"
      // banner). Merely hiding the overlay would reveal that stale, errored
      // dashboard. A hard navigation to '/' remounts the whole app so every
      // request refetches with the now-accepted state, and RoleBasedRedirect
      // drops the user on their own dashboard. Keep `submitting` true so the
      // button stays disabled through the redirect.
      notify.success('تم حفظ موافقتك. جارٍ تحويلك إلى لوحتك...');
      window.location.assign('/');
    } catch (err) {
      notify.error(err.response?.data?.message || 'تعذّر حفظ الموافقة، حاول مرة أخرى');
      setSubmitting(false);
    }
  };

  const showGate = gated && loaded && pending.length > 0;

  return (
    <>
      {children}
      {showGate && (
        <ModalSurface label="الموافقة على شروط الكلية" size="lg" className="z-[100] flex flex-col bg-card">
            <div className="px-4 sm:px-6 py-5 border-b border-border flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold break-words">الموافقة على شروط الكلية</h2>
                <p className="text-sm text-muted-foreground break-words">يرجى قراءة الشروط التالية والموافقة عليها للمتابعة.</p>
              </div>
            </div>

            <div className="min-h-0 px-4 sm:px-6 py-4 space-y-4 overflow-y-auto overflow-x-hidden overscroll-contain">
              {pending.map((a) => (
                <div key={a._id} className="rounded-xl border border-border overflow-hidden">
                  <div className="px-4 py-3 border-b border-border font-semibold break-words">{a.title}</div>
                  <div
                    dir="rtl"
                    className="rte-content px-4 py-3 max-h-48 overflow-y-auto overflow-x-hidden break-words text-sm text-foreground/90 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: bodyToHtml(a.body, a.bodyFormat) }}
                  />
                  <label className="flex items-start gap-2 px-4 py-3 border-t border-border bg-muted/40 cursor-pointer text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={!!checked[a._id]}
                      onChange={(e) => setChecked((c) => ({ ...c, [a._id]: e.target.checked }))}
                      className="h-4 w-4 rounded border-border shrink-0 mt-0.5"
                    />
                    <span className="min-w-0 break-words">لقد قرأت ووافقت على «{a.title}»</span>
                  </label>
                </div>
              ))}
            </div>

            <div className="px-4 sm:px-6 py-4 border-t border-border">
              <button
                onClick={submit}
                disabled={!allChecked || submitting}
                className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'جاري الحفظ...' : 'أوافق وأتابع'}
              </button>
              {!allChecked && (
                <p className="text-center text-xs text-muted-foreground mt-2">
                  يجب الموافقة على جميع الشروط للمتابعة.
                </p>
              )}
            </div>
        </ModalSurface>
      )}
    </>
  );
}
